import {
  type EventBusDomainEventMap,
  type EventBusConnectionStateEnum,
  type EventBusEventKey,
  type EventBusEventMap,
  type EventBusTransportError,
  type EventBusTransportErrorCodeEnum,
  type EventBusTransportEventMap,
  type IEventBus,
  EventBusConnectionStates,
  EventBusTransportErrorCodes,
  EventBusTransportEventTypes,
} from '@webapp/infrastructure/interfaces/services/IEventBus'
import { parseSSE } from './parseSSE'
import {
  type SSEFrame,
  type SseEventBusOptions,
  type SseEventHandler,
  type SseEventHandlerSet,
} from './types'

export class SseEventBus<
  T extends EventBusDomainEventMap = EventBusDomainEventMap,
> implements IEventBus<T> {
  private static readonly HTTP_UNAUTHORIZED = 401
  private static readonly HTTP_FORBIDDEN = 403

  private readonly url: string
  private readonly body: unknown
  private readonly headers: Record<string, string>
  private controller?: AbortController

  private readonly domainEventHandlers = new Map<
    keyof T,
    SseEventHandlerSet<T[keyof T]>
  >()
  private readonly transportStateEventHandlers = new Set<
    SseEventHandler<EventBusTransportEventMap['state']>
  >()
  private readonly transportErrorEventHandlers = new Set<
    SseEventHandler<EventBusTransportEventMap['error']>
  >()

  constructor(options: SseEventBusOptions) {
    this.url = options.url
    this.body = options.body
    this.headers = options.headers ?? {}
  }

  connect(): void {
    if (this.controller) {
      return
    }
    this.controller = new AbortController()
    void this.consume(this.controller.signal)
  }

  disconnect(): void {
    this.controller?.abort()
    this.controller = undefined

    this.domainEventHandlers.clear()
    this.transportStateEventHandlers.clear()
    this.transportErrorEventHandlers.clear()
  }

  bind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void {
    const eventName = String(event)

    if (eventName === EventBusTransportEventTypes.STATE) {
      this.transportStateEventHandlers.add(
        callback as SseEventHandler<EventBusTransportEventMap['state']>
      )
      return
    }

    if (eventName === EventBusTransportEventTypes.ERROR) {
      this.transportErrorEventHandlers.add(
        callback as SseEventHandler<EventBusTransportEventMap['error']>
      )
      return
    }

    let handlerSet = this.domainEventHandlers.get(event)
    if (!handlerSet) {
      handlerSet = new Set()
      this.domainEventHandlers.set(event, handlerSet)
    }
    handlerSet.add(callback as SseEventHandler<T[keyof T]>)
  }

  unbind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void {
    const eventName = String(event)

    if (eventName === EventBusTransportEventTypes.STATE) {
      this.transportStateEventHandlers.delete(
        callback as SseEventHandler<EventBusTransportEventMap['state']>
      )
      return
    }

    if (eventName === EventBusTransportEventTypes.ERROR) {
      this.transportErrorEventHandlers.delete(
        callback as SseEventHandler<EventBusTransportEventMap['error']>
      )
      return
    }

    const handlerSet = this.domainEventHandlers.get(event)
    if (!handlerSet) {
      return
    }
    handlerSet.delete(callback as SseEventHandler<T[keyof T]>)
    if (handlerSet.size === 0) {
      this.domainEventHandlers.delete(event)
    }
  }

  unbindAll(event?: EventBusEventKey<T>): void {
    if (!event) {
      this.domainEventHandlers.clear()
      this.transportStateEventHandlers.clear()
      this.transportErrorEventHandlers.clear()
      return
    }

    const eventName = String(event)

    if (eventName === EventBusTransportEventTypes.STATE) {
      this.transportStateEventHandlers.clear()
      return
    }

    if (eventName === EventBusTransportEventTypes.ERROR) {
      this.transportErrorEventHandlers.clear()
      return
    }

    this.domainEventHandlers.delete(event)
  }

  private async consume(signal: AbortSignal): Promise<void> {
    this.emitTransportStateEvent(EventBusConnectionStates.CONNECTING)

    const outcome = await this.openAndStream(signal)

    if (this.controller?.signal === signal) {
      this.controller = undefined
    }

    if (outcome.aborted) {
      return
    }
    if (outcome.error) {
      this.emitTransportErrorEvent(outcome.error)
    }
    const reconnectStarted = this.controller !== undefined
    if (!reconnectStarted) {
      this.emitTransportStateEvent(EventBusConnectionStates.DISCONNECTED)
    }
  }

  private async openAndStream(
    signal: AbortSignal
  ): Promise<{ aborted?: boolean; error?: EventBusTransportError }> {
    let response: Response
    try {
      response = await fetch(this.url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...this.readCsrfHeader(),
          ...this.headers,
        },
        body: JSON.stringify(this.body ?? {}),
        signal,
      })
    } catch (error) {
      if (signal.aborted) {
        return { aborted: true }
      }
      return {
        error: {
          code: EventBusTransportErrorCodes.CONNECTION_ERROR,
          message: 'Failed to open the event stream',
          cause: error,
        },
      }
    }

    if (!response.ok || response.body === null) {
      return {
        error: {
          code: this.errorCodeForStatus(response.status),
          message: `Event stream returned status ${response.status}`,
        },
      }
    }

    this.emitTransportStateEvent(EventBusConnectionStates.CONNECTED)

    try {
      for await (const frame of parseSSE(response.body)) {
        this.dispatchFrame(frame)
      }
      return {}
    } catch (error) {
      if (signal.aborted) {
        return { aborted: true }
      }
      return {
        error: {
          code: EventBusTransportErrorCodes.CONNECTION_ERROR,
          message: 'Event stream interrupted',
          cause: error,
        },
      }
    }
  }

  private dispatchFrame(frame: SSEFrame): void {
    const handlerSet = this.domainEventHandlers.get(frame.event)
    if (!handlerSet || handlerSet.size === 0) {
      return
    }

    let data: T[keyof T]
    try {
      data = JSON.parse(frame.data) as T[keyof T]
    } catch (error) {
      this.emitTransportErrorEvent({
        code: EventBusTransportErrorCodes.UNKNOWN_ERROR,
        message: `Failed to parse data for "${frame.event}" event`,
        cause: error,
      })
      return
    }

    for (const handler of handlerSet) {
      handler(data)
    }
  }

  private emitTransportStateEvent(state: EventBusConnectionStateEnum): void {
    const payload = { state }
    for (const handler of this.transportStateEventHandlers) {
      handler(payload)
    }
  }

  private emitTransportErrorEvent(payload: EventBusTransportError): void {
    for (const handler of this.transportErrorEventHandlers) {
      handler(payload)
    }
  }

  private errorCodeForStatus(status: number): EventBusTransportErrorCodeEnum {
    if (
      status === SseEventBus.HTTP_UNAUTHORIZED ||
      status === SseEventBus.HTTP_FORBIDDEN
    ) {
      return EventBusTransportErrorCodes.AUTH_ERROR
    }
    return EventBusTransportErrorCodes.CONNECTION_ERROR
  }

  private readCsrfHeader(): Record<string, string> {
    if (typeof document === 'undefined') {
      return {}
    }
    const match = /(?:^|;\s*)csrftoken=([^;]+)/.exec(document.cookie)
    return match ? { 'X-CSRFToken': decodeURIComponent(match[1]) } : {}
  }
}
